import React from 'react';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import {
    SlideContainer, SlideTitle, SlideSubtitle, SlideText, BulletList, BulletItem, HighlightBox, IconCircle,
    welcomeSlide, historicSlide, finalSlide,
} from 'components/v3/onboarding/OnboardingSlideElements';

// ==========================================
// Profil NETWORK — admin / account-manager / project-manager
// Atterrissage : /my-network. Voit le groupe « Mon réseau » au complet.
// ==========================================

export const networkSlides = () => [
    welcomeSlide(
        "Vos pages sont regroupées dans le menu sous « Mon réseau » : une vue d'ensemble, puis le détail par lieu, par flux, et l'historique complet.",
    ),

    // Mon réseau
    {
        title: '1 · Mon réseau',
        content: (
            <SlideContainer>
                <SlideTitle>
                    <IconCircle><HubOutlinedIcon /></IconCircle>
                    1 · Mon réseau
                </SlideTitle>
                <SlideText>
                    C'est votre page de <strong>vue globale</strong> sur <strong>l'état du réseau</strong> : tous lieux confondus.
                    <br />Vous y retrouvez des indicateurs de performance selon la période sélectionnée, ainsi que des indicateurs sur l'état figé du réseau.
                    <br />Vous avez accès à des informations de synthèse sur vos flux et vos emballages, avec la possibilité de filtrer par période et par flux.
                </SlideText>

                <SlideSubtitle>Sur la période sélectionnée</SlideSubtitle>
                <BulletList>
                    <BulletItem>
                        <strong>Taux de retour</strong> — nombre d'emballages récupérés sur le nombre d'emballages expédiés.
                    </BulletItem>
                    <BulletItem>
                        <strong>Cumul des expéditions</strong> — le nombre d'emballages expédiés sur la période choisie.
                    </BulletItem>
                    <BulletItem>
                        <strong>Cycle moyen</strong> — le temps que met un emballage entre son départ et son retour en entrepôt. <i>(disponible pour les flux fermés et professionnels uniquement)</i>
                    </BulletItem>
                </BulletList>

                <SlideSubtitle>État du réseau à l'instant T (fin de la période sélectionnée)</SlideSubtitle>
                <BulletList>
                    <BulletItem>
                        <strong>Cycle moyen / emballage</strong> — nombre d'utilisations moyen par emballage, sur la période choisie. <i>(disponible pour les flux fermés et professionnels uniquement)</i>
                    </BulletItem>
                    <BulletItem>
                        <strong>Flotte totale</strong> — la totalité des emballages qui vous sont attribués à un instant T.
                    </BulletItem>
                    <BulletItem>
                        <strong>Taux de réemploi</strong> — la part d'emballages réutilisés sur la période choisie, par rapport à la totalité des emballages expédiés (jetables compris, ou réemployables mais non réemployés).
                    </BulletItem>
                </BulletList>

                <SlideSubtitle>Les graphiques</SlideSubtitle>
                <BulletList>
                    <BulletItem>
                        Le <strong>graphique de volume</strong> en bas compare, mois par mois, ce qui est expédié, ce qui est retourné et la consommation en jetable.
                    </BulletItem>
                </BulletList>

                <SlideSubtitle>Régler l'affichage</SlideSubtitle>
                <BulletList>
                    <BulletItem>
                        Le <strong>sélecteur de période</strong> en haut de page s'applique à toutes les cartes
                        (aujourd'hui, cette semaine, ce mois-ci, une plage sur mesure). Vous avez accès à maximum 1 an de données.
                    </BulletItem>
                    <BulletItem>
                        Le <strong>sélecteur de flux</strong> permet d'isoler un seul flux.
                    </BulletItem>
                </BulletList>

                <HighlightBox>
                    <SlideText sx={{ marginBottom: 0 }}>
                        ✏️ Les petits crayons vous permettent de fixer vos <strong>cibles</strong> (taux de retour,
                        cycle moyen, taux de réemploi). Les cartes se situent ensuite par rapport à cet objectif.
                    </SlideText>
                </HighlightBox>
            </SlideContainer>
        ),
    },

    // Par lieu
    {
        title: '2 · Par lieu',
        content: (
            <SlideContainer>
                <SlideTitle>
                    <IconCircle><RoomOutlinedIcon /></IconCircle>
                    2 · Par lieu
                </SlideTitle>
                <SlideText>
                    Une vue <strong>site par site</strong> pour contrôler l'activité opérationnelle. La page se compose de deux blocs :
                </SlideText>

                <BulletList>
                    <BulletItem>
                        <strong>Vue Amont</strong> — vue sur <strong>l'ensemble de vos entrepôts</strong> : stock, taux de retour, stock dormant. Pour plus de détails, vous pouvez cliquer sur un entrepôt pour ouvrir le panneau latéral. <i>Cette vue est également disponible seule depuis le menu « Vue amont ».</i>
                    </BulletItem>
                    <BulletItem>
                        <strong>Vue Aval</strong> — vue sur <strong>l'ensemble de votre réseau de distribution</strong> : magasins, livraisons en point relais, ce qui arrive et ce qui est restitué. <i>Cette vue est également disponible seule depuis le menu « Vue aval ».</i>
                    </BulletItem>
                </BulletList>

                <HighlightBox>
                    <SlideText sx={{ marginBottom: 0 }}>
                        📅 Cette page couvre <strong>tout l'historique des lieux</strong> sur 12 mois, sans sélecteur de période :
                        c'est une photo de la situation, pas une mesure sur un mois donné.
                    </SlideText>
                </HighlightBox>
            </SlideContainer>
        ),
    },

    // Par flux
    {
        title: '3 · Par flux',
        content: (
            <SlideContainer>
                <SlideTitle>
                    <IconCircle><AccountTreeOutlinedIcon /></IconCircle>
                    3 · Par flux
                </SlideTitle>
                <SlideText>
                    Cette page sert à <strong>comparer vos flux entre eux</strong> pour repérer celui qui décroche et surveiller l'état de retour.
                </SlideText>

                <SlideSubtitle>Choisir ce que vous comparez</SlideSubtitle>
                <BulletList>
                    <BulletItem>
                        Les <strong>étiquettes de flux</strong> s'activent et se désactivent au clic
                        (il en reste toujours au moins une sélectionnée).
                    </BulletItem>
                </BulletList>

                <SlideSubtitle>Pour chaque flux</SlideSubtitle>
                <BulletList>
                    <BulletItem>
                        <strong>Taux de retour</strong> — nombre d'emballages récupérés sur le nombre d'emballages expédiés.
                    </BulletItem>
                    <BulletItem>
                        <strong>Volume expédié (YTD)</strong> — ce qui est parti depuis le 1er janvier.
                    </BulletItem>
                    <BulletItem>
                        <strong>Part de flotte</strong> — quelle proportion de vos emballages ce flux mobilise.
                    </BulletItem>
                </BulletList>
                <HighlightBox>
                    <SlideText sx={{ marginBottom: 0 }}>
                        💡 Cliquez sur une carte pour voir le détail des expéditions et réceptions de ce flux, sur la période choisie.
                    </SlideText>
                </HighlightBox>
            </SlideContainer>
        ),
    },

    // La slide « Opérationnel » a ete retiree (TASK-570) : cette page, obsolete en V3, ne figure
    // plus au menu. Le tutoriel ne doit pas decrire un ecran que le client ne peut plus ouvrir.
    // Validee par Donia le 05/08.

    historicSlide(4),

    // Paramètres
    {
        title: '5 · Paramètres',
        content: (
            <SlideContainer>
                <SlideTitle>
                    <IconCircle><SettingsIcon /></IconCircle>
                    5 · Paramètres
                </SlideTitle>
                <SlideText>
                    Cette page regroupe <strong>tous les réglages de votre compte et de votre organisation</strong>.
                </SlideText>

                <BulletList>
                    <BulletItem>
                        <strong>Organisation</strong> et <strong>Membres</strong> — les informations sur votre organisation et la liste des membres ayant accès à la plateforme.
                    </BulletItem>
                    <BulletItem>
                        <strong>Mes lieux</strong> et <strong>Mes flux</strong> — vos sites, et différents flux utilisés.
                    </BulletItem>
                    <BulletItem>
                        <strong>Config MOBIUScan</strong> et <strong>Intégration</strong> — configuration des actions sur l'application de scan (connexion, événements de scan).
                    </BulletItem>
                    <BulletItem>
                        <strong>Catégorisation des anomalies</strong> — vos catégories d'anomalie pouvant être déclarées par vos équipes sur l'application de scan.
                    </BulletItem>
                    <BulletItem>
                        <strong>Consommation en jetable</strong> — la saisie de vos emballages à usage unique,
                        indispensable au calcul du taux de réemploi.
                    </BulletItem>
                    <BulletItem>
                        <strong>Mon compte</strong> — vos informations personnelles.
                    </BulletItem>
                </BulletList>

                <SlideSubtitle>Vos seuils</SlideSubtitle>
                <SlideText>
                    Dans <strong>Organisation → Configuration</strong> : au bout de combien de jours un emballage est considéré comme dormant, et le nombre de rotations couvertes par notre garantie.
                </SlideText>

                <HighlightBox>
                    <SlideText sx={{ marginBottom: 0 }}>
                        ✏️ D'autres seuils se règlent directement sur les pages concernées, via un petit crayon :
                        l'objectif d'expéditions du jour, la capacité du Master Contenant et vos cibles de « Mon réseau ».
                    </SlideText>
                </HighlightBox>
            </SlideContainer>
        ),
    },

    // La page « Conformité ESG » n'a volontairement pas sa propre slide : c'est un ecran
    // d'attente avec une enquete, il n'y a pas encore de contenu a expliquer. Une simple
    // mention suffit — et elle n'est montree qu'aux roles qui voient cette entree de menu.
    finalSlide(
        <HighlightBox>
            <SlideText sx={{ marginBottom: 0 }}>
                🌱 <strong>Conformité ESG</strong> — cette page est en préparation. Une courte enquête vous y attend :
                vos réponses orienteront ce qu'elle affichera.
            </SlideText>
        </HighlightBox>,
    ),
];
